import { useFormik } from "formik";
import { useEffect, useState } from "react";
import ModalComponent from "..";
import {
  useInviteEventCoOrganizerMutation,
  useRemoveEventCoOrganizerMutation,
  useUpdateEventCoOrganizerMutation,
} from "../../../redux/features/event/event.api";
import {
  CoOrganizerRole,
  SingleCoOrganizer,
} from "../../../redux/features/event/event.types";
import { ModalCompProps } from "../../../utils/types";
import { InviteCoOrganizerSchema } from "../../../utils/validations";
import { Button } from "../../buttons/button";
import { ProfileIcon } from "../../cards/profilePic";
import { Icon } from "../../icon/icon";
import { Checkbox } from "../../inputs/checkbox";
import { TextInput } from "../../inputs/textInput";
import { MiniHeader } from "../../typography/miniHeader";

export const ManageCoOrganizerModal = ({
  open,
  handleClose,
  organizer,
  onSuccess,
  eventId,
}: ModalCompProps & {
  organizer?: SingleCoOrganizer;
  onSuccess: () => void;
  eventId: string;
}) => {
  const [mode, setMode] = useState<"create" | "edit">("create");

  const [inviteCoOrganizer, { isLoading: sendingInvite }] =
    useInviteEventCoOrganizerMutation();

  const [updateCoOrganizer, { isLoading: updatingInvite }] =
    useUpdateEventCoOrganizerMutation();

  const [removeCoOrganizer, { isLoading: removingCoOrganizer }] =
    useRemoveEventCoOrganizerMutation();

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleBlur,
    handleChange,
    resetForm,
    isValid,
    setFieldValue,
  } = useFormik({
    initialValues: {
      email: organizer?.email ?? "",
      role: organizer?.role ?? undefined,
    } as {
      email: string;
      role?: CoOrganizerRole;
    },
    validationSchema: InviteCoOrganizerSchema,
    onSubmit: async (vals) => {
      if (mode === "create") {
        const resp = await inviteCoOrganizer({
          eventId: eventId,
          payload: {
            email: vals.email,
            role: vals.role!,
          },
        });

        if (resp.data?.status === "success") {
          resetForm();
          onSuccess();
          handleClose();
        }
      } else {
        const resp = await updateCoOrganizer({
          eventId: eventId,
          payload: {
            coOrganizerEmail: vals.email,
            newRole: vals.role!,
          },
        });

        if (resp.data?.status === "success") {
          resetForm();
          onSuccess();
          handleClose();
        }
      }
    },
  });

  useEffect(() => {
    if (!!organizer) {
      setMode("edit");
      setFieldValue("email", organizer.email);
      setFieldValue("role", organizer.role);
    } else {
      setMode("create");
    }
    // eslint-disable-next-line
  }, [organizer, open]);

  return (
    <ModalComponent
      open={open}
      handleClose={() => {
        handleClose();
        resetForm();
      }}
      className=""
      dialogClassName=""
      closeOnOverlayClick
      showHeader={false}
      title={undefined}
      subtitle={undefined}
      showBackIcon={false}
      mobileAnchorBottom
      showCloseIcon={false}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex items-start justify-between">
          <Icon
            svg={"user-plus"}
            width={42}
            height={42}
            className="rounded-xl border border-grey-100 p-[11px] text-primary"
          />

          {mode === "edit" && (
            <button
              type="button"
              className="w-fit"
              onClick={() => {
                handleClose();
                resetForm();
              }}
            >
              <Icon svg={"close-icon"} width={24} height={24} />
            </button>
          )}
        </div>

        <div>
          <MiniHeader
            header={`${mode === "create" ? "Add" : "Update"} Co-organiser`}
            subheader={
              mode === "create"
                ? "Add a co-organizer to get help managing your event. We’d send them an email invite to get setup"
                : undefined
            }
          />
        </div>

        {mode === "create" ? (
          <TextInput
            type="text"
            value={values.email}
            hasTitle
            title="Email"
            placeholder="Enter co-organizer’s email"
            onBlur={handleBlur}
            onChange={handleChange}
            name="email"
            readOnly={false}
            errors={touched.email && errors.email ? errors.email : undefined}
          />
        ) : (
          <div className="flex w-full items-center gap-3 rounded-2xl border border-grey-200 p-3">
            <ProfileIcon img="" dimension={32} className="" />

            <div className="flex-1">
              <p className="text-sm font-bold text-grey-900">
                {organizer?.status === "accepted"
                  ? `${organizer.userId.firstName} ${organizer.userId.lastName}`
                  : "Pending Invite"}
              </p>
              <p className="text-sm text-grey-600">{organizer?.email}</p>
            </div>

            {organizer?.status === "invited" && (
              <Button
                type="button"
                className="!w-fit !border-none !p-0 !text-sm !font-bold !text-primary"
                text="Resend Invite"
                variation="bland"
              />
            )}
          </div>
        )}

        <div className="flex flex-col gap-[6px]">
          <p className="mb-[2px] text-sm font-bold text-grey-800">
            Event Access
          </p>

          <Checkbox
            text={
              <>
                <Icon
                  svg="avatar"
                  width={24}
                  height={24}
                  className="text-grey-600"
                />

                <p className="flex flex-1 flex-col">
                  <span>Manager</span>
                  <span className="font-normal text-grey-500">
                    They would have full access to this event
                  </span>
                </p>
              </>
            }
            textPosition="left"
            handleChange={() => {
              setFieldValue("role", "manager");
            }}
            className="!flex !w-full !gap-5 rounded-2xl bg-grey-50 p-3 !text-left"
            disabled={false}
            checkStatus={values.role === "manager"}
            textClassName="!flex-1 !flex items-center gap-5"
            checkDimension={20}
          />

          <Checkbox
            text={
              <>
                <Icon
                  svg="scan-line"
                  width={24}
                  height={24}
                  className="text-grey-600"
                />

                <p className="flex flex-1 flex-col">
                  <span>Check-In only</span>
                  <span className="font-normal text-grey-500">
                    They can only view guest list and check-in guests
                  </span>
                </p>
              </>
            }
            textPosition="left"
            handleChange={() => {
              setFieldValue("role", "check-in-only");
            }}
            className="!flex !w-full !gap-5 rounded-2xl bg-grey-50 p-3 !text-left"
            disabled={false}
            checkStatus={values.role === "check-in-only"}
            textClassName="!flex-1 !flex items-center gap-5"
            checkDimension={20}
          />
        </div>

        <div className="flex w-full items-center justify-end gap-2">
          <Button
            text={mode === "create" ? "Cancel" : "Remove"}
            type="button"
            onClick={async () => {
              if (mode === "create") {
                handleClose();
                resetForm();
              } else {
                const resp = await removeCoOrganizer({
                  eventId: eventId,
                  payload: {
                    coOrganizerEmail: organizer!.email,
                  },
                });

                if (resp.data?.status === "success") {
                  resetForm();
                  onSuccess();
                  handleClose();
                }
              }
            }}
            variation={mode === "create" ? "plain-grey" : "plain-danger"}
            className="max-w-fit"
            loading={removingCoOrganizer}
            disabled={removingCoOrganizer}
          />

          <Button
            text={mode === "create" ? "Send Invite" : "Update"}
            type="submit"
            onClick={() => {}}
            className="max-w-fit"
            loading={
              (mode === "create" && sendingInvite) ||
              (mode === "edit" && updatingInvite)
            }
            disabled={
              !isValid ||
              (mode === "create" && sendingInvite) ||
              (mode === "edit" && updatingInvite)
            }
            variation={"primary"}
          />
        </div>
      </form>
    </ModalComponent>
  );
};
